import React, { useEffect, useRef, useState } from 'react';
import { pad2, prefersReducedMotion } from '../../../../utils';
import SectionWrapper from '../../../../components/sectionWrapper/sectionWrapper';
import { SLIDES, Slide } from './slides';
import { styles } from './styles';

function Plate(props: {
  slide: Slide;
}): JSX.Element {

  return (
    <div className="pc-plate" style={styles.plate}>
      <img className="pc-shot-dark" src={props.slide.screens.dark} alt={`PlayerCafe: ${props.slide.title}`} loading="lazy" style={styles.shot} />
      <img className="pc-shot-light" src={props.slide.screens.light} alt={`PlayerCafe: ${props.slide.title}`} loading="lazy" style={styles.shot} />
    </div>
  );

}

export default function Tour(): JSX.Element {

  const [current, setCurrent] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isFirst = current === 0;
  const isLast = current === SLIDES.length - 1;

  const goTo = (index: number): void => {

    const track = trackRef.current;
    const slide = slideRefs.current[Math.max(0, Math.min(SLIDES.length - 1, index))];

    if (!track || !slide) {
      return;
    }

    const left = slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;

    track.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(current + 1);
    }
    else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(current - 1);
    }
  };

  useEffect(() => {

    const track = trackRef.current;

    if (!track) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setCurrent(slideRefs.current.indexOf(entry.target as HTMLDivElement));
        }
      });
    }, { root: track, threshold: 0.6 });

    slideRefs.current.forEach((slide) => slide && observer.observe(slide));

    return () => observer.disconnect();
  }, []);

  return (
    <SectionWrapper
      id="tour"
      label="How it works"
      heading="From sign-up to your first game"
      sub="Every screen, in the order you'll meet it."
     
    >
      <div style={styles.controls}>
        <span style={styles.counter} aria-live="polite">
          <span style={styles.counterCurrent}>{pad2(current)}</span> / {pad2(SLIDES.length - 1)}
        </span>
        <button
          style={isFirst ? styles.arrowDisabled : styles.arrow}
          onClick={() => goTo(current - 1)}
          disabled={isFirst}
          aria-label="Previous screen"
        >
          ←
        </button>
        <button
          style={isLast ? styles.arrowDisabled : styles.arrow}
          onClick={() => goTo(current + 1)}
          disabled={isLast}
          aria-label="Next screen"
        >
          →
        </button>
      </div>

      <div
        ref={trackRef}
        className="pc-tour-track"
        style={styles.track}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="How PlayerCafe works"
        onKeyDown={onKeyDown}
      >
        {SLIDES.map((slide, index) => (
          <div
            key={slide.title}
            ref={(element) => { slideRefs.current[index] = element; }}
            className="pc-tour-slide"
            style={styles.slide}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${SLIDES.length}: ${slide.title}`}
          >
            <Plate slide={slide} />
            <div className="pc-tour-copy">
              <span style={styles.index}>{pad2(index)}</span>
              <h3 style={styles.title}>{slide.title}</h3>
              <p style={styles.lead}>{slide.lead}</p>
              <ul style={styles.points}>
                {slide.points.map((point) => (
                  <li key={point} style={styles.point}>
                    <span style={styles.marker} aria-hidden />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              {slide.gesture && <span style={styles.gesture}>{slide.gesture}</span>}
            </div>
          </div>
        ))}
      </div>

      <div style={styles.dots}>
        {SLIDES.map((slide, index) => (
          <button
            key={slide.title}
            style={styles.dot}
            onClick={() => goTo(index)}
            aria-label={`Go to screen ${index + 1}: ${slide.title}`}
            aria-current={index === current ? 'true' : undefined}
          >
            <span style={index === current ? styles.dotMarkActive : styles.dotMark} />
          </button>
        ))}
      </div>
    </SectionWrapper>
  );

}
